import { computed, toValue, type MaybeRef } from 'vue';
import {
  DESIGN_ASSET_TYPES,
  PRODUCTION_ASSET_TYPES,
  QUOTING_ASSET_TYPES,
} from '@cutr/constants/order';
import { storeToRefs } from 'pinia';
import { useOrderFiles } from '@/stores/orderFiles';
import { useRouteIds } from '@/components/elements/Order/composables';
import { usePositionsStore } from '@/stores/positions';

export function useActivePosition(id?: MaybeRef<string>) {
  const { positionId, fileId } = useRouteIds();

  const fileStore = useOrderFiles();
  const { allPositions, positions } = storeToRefs(usePositionsStore());

  const activePositionId = computed(() => {
    return id ? toValue(id) : positionId.value;
  });

  const activePosition = computed(() => {
    const id = activePositionId.value;
    if (!id) return;
    return allPositions.value.find((p) => p.id === id);
  });

  const positionIsArchived = computed(() =>
    Boolean(activePosition.value?.archivedAt)
  );

  const positionFiles = computed(() => {
    // only use archived files if position is also archived
    const files = positionIsArchived.value
      ? fileStore.allFiles
      : fileStore.files;

    return files.filter((file) => file.positionId === activePositionId.value);
  });

  const designFiles = computed(() =>
    positionFiles.value.filter((f) => DESIGN_ASSET_TYPES.includes(f.assetType))
  );
  const pricingFiles = computed(() =>
    positionFiles.value.filter((f) => QUOTING_ASSET_TYPES.includes(f.assetType))
  );
  const productionFiles = computed(() =>
    positionFiles.value.filter((f) =>
      PRODUCTION_ASSET_TYPES.includes(f.assetType)
    )
  );

  const positionsWithDesignFiles = computed(() => {
    const designFiles = fileStore.files.filter((f) =>
      DESIGN_ASSET_TYPES.includes(f.assetType)
    );

    return positions.value.filter((position) =>
      designFiles.some((file) => file.positionId === position.id)
    );
  });

  const selectedPositionFile = computed(() => {
    return positionFiles.value.find((f) => f.id === fileId.value);
  });

  return {
    positions,
    activePosition,
    designFiles,
    pricingFiles,
    positionFiles,
    productionFiles,
    selectedPositionFile,
    positionsWithDesignFiles,
  };
}
